import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Product, Variant } from '@nucleus-commerce/api-client/admin';
import type { Money } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useProductStock } from '@/features/inventory';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { cellPath, type VariantRowsInput } from '../api/variant-mappers';
import { useVariantRowsForm } from '../hooks/use-variant-rows-form';

import { SetVariantPriceDialog } from './set-variant-price-dialog';
import { SkuOriginChip } from './sku-origin-chip';
import { MoneyCell, TextCell } from './variant-table-cells';
import { VariantsCard, type VariantColumn } from './variants-card';
import {
  DeleteVariantsDialog,
  VariantFooterActions,
  VariantStatusBulkActions,
  WatchedVariantStatus,
} from './variants-card-actions';
import { MasterVariantDetails } from './variants-card-details';
import { StockFigure } from './variants-card-parts';

/** A group's price range, from the rows' current drafts. */
function PriceRange({ variants }: { variants: readonly Variant[] }) {
  const format = useFormat();
  const { control } = useFormContext<VariantRowsInput>();
  // A row not in the form yet, or leaving it, reads undefined.
  const prices: ReadonlyArray<Money | null | undefined> = useWatch({
    control,
    name: variants.map((variant) => cellPath(variant.id, 'price')),
  });
  const known = prices.filter((price) => price != null);
  const [first] = known;
  if (!first) return null;
  const amounts = known.map((price) => price.amount);
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);
  const money = (amount: number) => format.money({ amount, currency: first.currency });
  return <>{low === high ? money(low) : `${money(low)} – ${money(high)}`}</>;
}

export interface MasterVariantsCardProps {
  product: Product;
  /** In position order. */
  variants: readonly Variant[];
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
}

/** The master product's Variants card: the row form, its columns, its bulk actions and footer. */
export function MasterVariantsCard({
  product,
  variants,
  isPending,
  error,
  onRetry,
}: MasterVariantsCardProps) {
  const { t } = useTranslation(['products', 'common']);
  const { can } = usePermissions();
  const stockReadable = can('inventory.read');
  const stockQuery = useProductStock(product.id, stockReadable);
  const rows = useVariantRowsForm(product, variants);
  const [pricing, setPricing] = useState<Variant[]>([]);

  const currency = variants[0]?.price.currency ?? BASE_CURRENCY;

  const stockRows = stockReadable ? (stockQuery.data ?? null) : null;
  const totals = useMemo(() => {
    const sums = new Map<string, number>();
    for (const row of stockRows ?? []) {
      sums.set(row.variantId, (sums.get(row.variantId) ?? 0) + row.available);
    }
    return sums;
  }, [stockRows]);
  const stockOf = (variant: Variant): number | null | 'loading' => {
    if (!stockReadable || !variant.trackInventory) return null;
    return stockQuery.isPending ? 'loading' : (totals.get(variant.id) ?? 0);
  };

  const columns: VariantColumn[] = [
    {
      id: 'price',
      header: t('products:variantsCard.columns.price', { currency }),
      width: '7.5rem',
      align: 'end',
      cell: (variant) => (
        <MoneyCell
          id={variant.id}
          field="price"
          currency={currency}
          label={t('products:variants.columns.priceFor', { variant: variant.title })}
        />
      ),
      summary: (list) => <PriceRange variants={list} />,
    },
    {
      id: 'sku',
      header: t('products:variants.columns.sku'),
      width: '11rem',
      cell: (variant) => (
        <TextCell
          id={variant.id}
          field="sku"
          label={t('products:variants.columns.skuFor', { variant: variant.title })}
          className="font-mono"
          after={variant.skuIsManual && variant.sku ? <SkuOriginChip origin="manual" /> : null}
        />
      ),
    },
    {
      id: 'stock',
      header: t('products:variantsCard.columns.stock'),
      width: '4.5rem',
      align: 'end',
      cell: (variant) => <StockFigure value={stockOf(variant)} />,
      summary: (list) => {
        const counted = list.map(stockOf).filter((stock) => typeof stock === 'number');
        return counted.length > 0
          ? t('products:variantsCard.stockTotal', {
              count: counted.reduce((sum, stock) => sum + stock, 0),
            })
          : '—';
      },
    },
  ];

  return (
    <Form {...rows.form}>
      <VariantsCard
        product={product}
        variants={rows.shown}
        // The render between a first list arriving and the form holding it.
        isPending={isPending || (rows.shown.length === 0 && variants.length > 0)}
        error={error}
        onRetry={onRetry}
        columns={columns}
        selectable={rows.canWrite}
        skuOf={rows.skuOf}
        errorIds={rows.errorIds}
        reveal={rows.reveal}
        renderStatus={(variant) => <WatchedVariantStatus id={variant.id} />}
        renderRowNote={(variant, expanded) => {
          const text = expanded ? null : rows.hiddenError(variant.id);
          return text ? (
            <p role="alert" className="text-destructive text-xs">
              {text}
            </p>
          ) : null;
        }}
        renderDetail={(variant) => (
          <MasterVariantDetails product={product} variant={variant} stock={stockRows} />
        )}
        renderBulk={(selected) => (
          <>
            <Button
              type="button"
              size="sm"
              className="h-8 text-[13px]"
              onClick={() => setPricing(selected)}
            >
              {t('products:variants.bulk.setPrice')}
            </Button>
            <VariantStatusBulkActions rows={rows} selected={selected} />
          </>
        )}
        notice={
          rows.problems.length > 0 ? (
            <div className="flex flex-col gap-2 px-4 pt-3">
              {rows.problems.map((text) => (
                <Alert key={text} variant="destructive" role="alert">
                  <AlertDescription>{text}</AlertDescription>
                </Alert>
              ))}
            </div>
          ) : null
        }
        emptyText={
          product.options.length > 0
            ? t('products:variantsCard.empty.withOptions')
            : t('products:variantsCard.empty.noOptions')
        }
        footer={
          <>
            <span className="min-w-0 flex-1 basis-72">{t('products:variantsCard.note')}</span>
            <VariantFooterActions product={product} variants={variants} />
          </>
        }
      />

      <SetVariantPriceDialog
        count={pricing.length}
        currency={currency}
        onApply={(price) => {
          rows.setPrice(pricing, price);
          setPricing([]);
        }}
        onClose={() => setPricing([])}
      />
      <DeleteVariantsDialog rows={rows} />
    </Form>
  );
}
